import { formatCurrency } from '@/utils/formatCurrency';
import {
  IST_HOUR_OPTIONS,
  exclusiveEndHourFromTimeLocal,
  exclusiveEndTimeLocalFromHour,
  formatIstHourLabel,
  hourFromTimeLocal,
  timeLocalFromHour,
  type HourlyBudgetBaseWindowDraft,
  type HourlyBudgetTimeWindowDraft,
} from '@/utils/hourlyBudgetSchedule';

export type HourSlotMap = Record<number, string>;

const MAX_WINDOWS = 12;
const MAX_SELECTED_HOUR = 23;

export const HOUR_MULTIPLIER_OPTIONS = ['1', '1.25', '1.5', '1.75', '2', '2.5', '3', '4', '5'];
export const CUSTOM_MULTIPLIER_VALUE = '__custom__';

export function formatHourMultiplier(value: string): string {
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed) || parsed <= 0) return '1×';
  const label = Number.isInteger(parsed) ? String(parsed) : String(parsed);
  return `${label}×`;
}

export function formatHourlyIncrementNote(hourlyBudgetInput: string): string {
  const trimmed = hourlyBudgetInput.trim();
  const amount = parseFloat(trimmed);
  if (!trimmed || Number.isNaN(amount) || amount < 0) {
    return 'Enter an hourly budget to see how 1× and higher multipliers apply per hour.';
  }
  const formatAmount = (value: number) => {
    const rounded = Math.round(value * 100) / 100;
    const digits = Number.isInteger(rounded) ? 0 : 2;
    return formatCurrency(rounded, undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  };
  const once = formatAmount(amount);
  const twice = formatAmount(amount * 2);
  return `Your base hourly increment is ${once}. A 1× multiplier uses ${once} increment, while 2× uses ${twice} increment.`;
}

export function selectedHoursFromSlots(slots: HourSlotMap): number[] {
  return Object.keys(slots)
    .map(Number)
    .filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= MAX_SELECTED_HOUR)
    .sort((a, b) => a - b);
}

export function activeHourCountForSlots(slots: HourSlotMap): number {
  return selectedHoursFromSlots(slots).length;
}

export function formatActiveHourCount(count: number): string {
  return `${count} hour${count === 1 ? '' : 's'}`;
}

function parseNonNegativeBudget(raw: string): number {
  const parsed = parseFloat(raw.trim());
  if (Number.isNaN(parsed) || parsed < 0) return 0;
  return parsed;
}

function parsePositiveHourlyBudget(raw: string): number | null {
  const parsed = parseFloat(raw.trim());
  if (Number.isNaN(parsed) || parsed <= 0) return null;
  return parsed;
}

function parsePositiveMultiplierValue(raw: string): number {
  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed) || parsed <= 0) return 1;
  return parsed;
}

/** Hourly increment for one selected slot: hourly budget × multiplier. */
export function hourIncrementAmount(hourlyRaw: string, multiplierRaw: string): number | null {
  const hourly = parsePositiveHourlyBudget(hourlyRaw);
  if (hourly == null) return null;
  const multiplier = parsePositiveMultiplierValue(multiplierRaw);
  return Math.round(hourly * multiplier * 100) / 100;
}

/** Base budget plus the sum of multiplier × hourly price for every selected slot. */
export function totalBudgetForSlots(
  slots: HourSlotMap,
  hourlyRaw: string,
  baseRaw: string
): number {
  const baseBudget = parseNonNegativeBudget(baseRaw);
  let hourlyTotal = 0;
  for (const hour of selectedHoursFromSlots(slots)) {
    const increment = hourIncrementAmount(hourlyRaw, slots[hour] ?? '1');
    if (increment != null) hourlyTotal += increment;
  }
  return Math.round((baseBudget + hourlyTotal) * 100) / 100;
}

/** Snapshot of current form fields so Save can stay disabled until something actually changes. */
export function hourlyBudgetFormSnapshot(
  hourlyBudgetInput: string,
  baseBudgetInput: string,
  active: boolean,
  hourSlots: HourSlotMap,
  extra?: {
    scheduleMode?: string;
    weeklyAmounts?: Record<number, { hourly: string; base: string }>;
    weeklySlots?: Record<number, HourSlotMap>;
  }
): string {
  const hours = selectedHoursFromSlots(hourSlots).map((hour) => [hour, hourSlots[hour] ?? '']);
  const weekly =
    extra?.scheduleMode === 'weekly'
      ? {
          scheduleMode: extra.scheduleMode,
          weeklyAmounts: extra.weeklyAmounts ?? {},
          weeklySlots: Object.fromEntries(
            Object.entries(extra.weeklySlots ?? {}).map(([day, slots]) => [
              day,
              selectedHoursFromSlots(slots).map((hour) => [hour, slots[hour] ?? '']),
            ])
          ),
        }
      : { scheduleMode: extra?.scheduleMode ?? 'daily' };
  return JSON.stringify({ hourlyBudgetInput, baseBudgetInput, active, hours, ...weekly });
}

function occupiedHoursInRange(start: number, end: number): number[] {
  const hours: number[] = [];
  for (let hour = start; hour < end; hour++) hours.push(hour);
  return hours;
}

export function hourSlotsFromWindows(
  bases: HourlyBudgetBaseWindowDraft[],
  times: HourlyBudgetTimeWindowDraft[]
): HourSlotMap {
  const slots: HourSlotMap = {};
  for (const row of bases) {
    const start = hourFromTimeLocal(row.start_time_local);
    const end = exclusiveEndHourFromTimeLocal(row.end_time_local, start);
    for (const hour of occupiedHoursInRange(start, end)) slots[hour] = '1';
  }
  for (const row of times) {
    const start = hourFromTimeLocal(row.start_time_local);
    const end = exclusiveEndHourFromTimeLocal(row.end_time_local, start);
    const multiplier = row.multiplier.trim() || '1';
    for (const hour of occupiedHoursInRange(start, end)) slots[hour] = multiplier;
  }
  return slots;
}

/** Collapse selected hours into half-open [start, end) ranges. Hour 23 (11:00 PM) is [23, 24). */
export function collapseHoursToRanges(hours: number[]): [number, number][] {
  const sorted = [...new Set(hours.filter((hour) => hour >= 0 && hour <= MAX_SELECTED_HOUR))].sort(
    (a, b) => a - b
  );
  const ranges: [number, number][] = [];
  for (const hour of sorted) {
    const last = ranges[ranges.length - 1];
    if (last && last[1] === hour) last[1] = hour + 1;
    else ranges.push([hour, hour + 1]);
  }
  return ranges;
}

function normalizeMultiplier(value: string): string {
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed) || parsed <= 0) return '1';
  return String(parsed);
}

function isBaseMultiplier(value: string): boolean {
  return parseFloat(normalizeMultiplier(value)) === 1;
}

export function windowsFromHourSlots(slots: HourSlotMap): {
  bases: HourlyBudgetBaseWindowDraft[];
  windows: HourlyBudgetTimeWindowDraft[];
} {
  const selected = selectedHoursFromSlots(slots);
  const bases = collapseHoursToRanges(selected).map(([start, end]) => ({
    clientId: crypto.randomUUID(),
    start_time_local: timeLocalFromHour(start),
    end_time_local: exclusiveEndTimeLocalFromHour(end),
  }));

  const windows: HourlyBudgetTimeWindowDraft[] = [];
  let i = 0;
  while (i < selected.length) {
    const multiplier = normalizeMultiplier(slots[selected[i]] ?? '1');
    if (isBaseMultiplier(multiplier)) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (
      j < selected.length &&
      selected[j] === selected[j - 1] + 1 &&
      normalizeMultiplier(slots[selected[j]] ?? '1') === multiplier
    ) {
      j += 1;
    }
    windows.push({
      clientId: crypto.randomUUID(),
      start_time_local: timeLocalFromHour(selected[i]),
      end_time_local: exclusiveEndTimeLocalFromHour(selected[j - 1] + 1),
      multiplier,
    });
    i = j;
  }

  return { bases, windows };
}

export function rangeCountForSlots(slots: HourSlotMap): number {
  return collapseHoursToRanges(selectedHoursFromSlots(slots)).length;
}

export function canToggleHourOn(slots: HourSlotMap, hour: number): boolean {
  if (slots[hour] != null) return true;
  return rangeCountForSlots({ ...slots, [hour]: '1' }) <= MAX_WINDOWS;
}

export function formatSelectedHourChip(hour: number, multiplier: string): string {
  return `${formatIstHourLabel(hour)} ${formatHourMultiplier(multiplier)}`;
}

export { IST_HOUR_OPTIONS, MAX_WINDOWS };
